import { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FloppyDisk } from "@phosphor-icons/react";
import { calculateTDEE, getAgeFromDob } from "@/lib/tdee";
import AnamnesiPersonalSection from "./anamnesi/AnamnesiPersonalSection";
import AnamnesiPhysicalSection from "./anamnesi/AnamnesiPhysicalSection";
import AnamnesiNutritionSection from "./anamnesi/AnamnesiNutritionSection";
import AnamnesiMedicalSection from "./anamnesi/AnamnesiMedicalSection";
import AnamnesiLifestyleSection from "./anamnesi/AnamnesiLifestyleSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NUMERIC_FIELDS = ["height_cm", "weight_kg"];
const INT_FIELDS = ["meals_per_day"];

function buildInitialForm(client) {
  return {
    first_name: client.first_name || "",
    last_name: client.last_name || "",
    email: client.email || "",
    phone: client.phone || "",
    date_of_birth: client.date_of_birth || "",
    gender: client.gender || "",
    height_cm: client.height_cm ?? "",
    weight_kg: client.weight_kg ?? "",
    pathologies: client.pathologies || "",
    allergies: client.allergies || "",
    objectives: client.objectives || "",
    medical_notes: client.medical_notes || "",
    lifestyle: client.lifestyle || "",
    activity_level: client.activity_level || "",
    notes: client.notes || "",
    diet_type: client.diet_type || "",
    food_intolerances: client.food_intolerances || "",
    food_allergies: client.food_allergies || "",
    meals_per_day: client.meals_per_day ?? "",
    supplements: client.supplements || "",
    nutrition_notes: client.nutrition_notes || "",
    caloric_status: client.caloric_status || "",
    sleep_quality: client.sleep_quality || "",
    sleep_hours: client.sleep_hours || "",
    motivation: client.motivation || "",
    expectations: client.expectations || "",
  };
}

function normalizePayload(form) {
  const payload = { ...form };
  NUMERIC_FIELDS.forEach((key) => {
    payload[key] = payload[key] !== "" ? parseFloat(payload[key]) : null;
  });
  INT_FIELDS.forEach((key) => {
    payload[key] = payload[key] !== "" ? parseInt(payload[key], 10) : null;
  });
  return payload;
}

export default function AnamnesiTab({ client, onUpdate }) {
  const [form, setForm] = useState(() => buildInitialForm(client));
  const [saving, setSaving] = useState(false);

  const handleChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/clients/${client.id}`, normalizePayload(form));
      toast.success("Anamnesi aggiornata");
      onUpdate();
    } catch (err) {
      console.error("[AnamnesiTab] save failed", err);
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const tdeeData = useMemo(() => {
    const age = getAgeFromDob(form.date_of_birth);
    const weight = parseFloat(form.weight_kg);
    const height = parseFloat(form.height_cm);
    return calculateTDEE(weight, height, age, form.gender, form.activity_level);
  }, [form.date_of_birth, form.weight_kg, form.height_cm, form.gender, form.activity_level]);

  return (
    <div
      className="bg-[#171717] border border-[#262626] rounded-lg p-6"
      data-testid="anamnesi-tab"
    >
      <div className="flex items-center justify-between mb-6">
        <h3
          className="text-xl font-bold tracking-tight uppercase text-white"
          style={{ fontFamily: "Barlow Condensed, sans-serif" }}
        >
          SCHEDA ANAMNESI
        </h3>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#007AFF] hover:bg-[#3395FF] text-white"
          data-testid="save-anamnesi-button"
        >
          <FloppyDisk size={18} className="mr-2" />
          {saving ? "Salvataggio..." : "Salva"}
        </Button>
      </div>

      <AnamnesiPersonalSection form={form} onChange={handleChange} />
      <AnamnesiPhysicalSection form={form} onChange={handleChange} tdeeData={tdeeData} />
      <AnamnesiNutritionSection form={form} onChange={handleChange} />
      <AnamnesiMedicalSection form={form} onChange={handleChange} />
      <AnamnesiLifestyleSection form={form} onChange={handleChange} />
    </div>
  );
}
